import {useEffect, useMemo, useState} from 'react';
import * as css from './TicketsSubscription.css';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {LinkButton} from '@tickchak/react-share/app';
import SubscriptionTableHeader from './subscriptionTableHeader/SubscriptionTableHeader';
import PlanRow from './planRow/PlanRow';
import SubscriptionSummary from './subscriptionSummary/SubscriptionSummary';
import {XHR_LOADING_STATE} from '../../../../../../assets/constants/constants';
import TicketsTemplate from '../TicketsTemplate';
import {SubscriptionPlan} from '../../../../../../redux/subscription/subscription.type';
import {getPlans} from '../../../../../../redux/subscription/subscriptionActions';
import useShow from '../../../../../../hooks/useShow';
import DetailsPop from './detailsPop/DetailsPop';
import LinkIcon from '../../../../../../assets/icons/LinkIcon';

function TicketsSubscription() {
    const {t} = useTranslation('realtime');
    const eid = useSelector((s: any) => s.event.eid);
    const plans = useSelector((s: any) => s.subscription?.subscriptionPlans || []);
    const getPlansXHR = useSelector((s: any) => s.subscription?.getPlansXHR);
    const isLoading = getPlansXHR === XHR_LOADING_STATE;
    
    const detailsPopShow = useShow();
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);

    const handleOpenDetails = (plan: SubscriptionPlan) => {
        setSelectedPlan(plan);
        detailsPopShow.open();
    };
    
    const handleCloseDetails = () => {
        detailsPopShow.close();
        setSelectedPlan(null);
    };

    useEffect(() => {
        if(eid && getPlansXHR === null) {
            getPlans().then();
        }
    }, [eid, getPlansXHR]);

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if(!plans.length) {
            return {
                totalSold: 0,
                totalAmount: 0,
                totalSeries: 0,
                totalEvents: 0,
                activeEventsCount: 0
            };
        }

        const stats = plans.reduce(
            (acc, plan: SubscriptionPlan) => {
                acc.totalSold += (plan.amount - plan.amountAvailable);
                acc.totalAmount += plan.amount;
                acc.totalEvents += plan.eventsCount;
                acc.activeEvents += plan.activeEventsCount;
                return acc;
            },
            {totalSold: 0, totalAmount: 0, totalEvents: 0, activeEvents: 0}
        );
        const {totalSold, totalAmount, totalEvents, activeEvents} = stats;

        return {
            totalSold,
            totalAmount,
            totalSeries: plans.length,
            totalEvents,
            activeEventsCount: activeEvents
        };
    }, [plans]);

    if(isLoading) {
        return <TicketsTemplate />;
    }

    return (
        <css.container>
            <css.title>
                {t('subscriptionWindowTitle')}
                <LinkButton
                    variant="brand"
                    size="category"
                    href={`/e/${eid}/subscription`}
                    iconEnd={<LinkIcon />}
                >
                    {t('seriesPage')}
                </LinkButton>
            </css.title>
            <css.content>
                <SubscriptionSummary
                    totalSold={summaryStats.totalSold}
                    totalAmount={summaryStats.totalAmount}
                    totalSeries={summaryStats.totalSeries}
                    totalEvents={summaryStats.totalEvents}
                    activeEventsCount={summaryStats.activeEventsCount}
                    plans={plans}
                />
                <css.tableSection>
                    <SubscriptionTableHeader />
                    <css.tableContent>
                        {plans.map((plan: SubscriptionPlan) => (
                            <PlanRow key={plan.planTid} plan={plan} onOpenDetails={handleOpenDetails} />
                        ))}
                    </css.tableContent>
                </css.tableSection>
            </css.content>
            {detailsPopShow.isOpen && selectedPlan && (
                <DetailsPop
                    plan={selectedPlan}
                    detailsPopShow={detailsPopShow}
                    onCloseDetails={handleCloseDetails}
                />
            )}
        </css.container>
    );
}

export default TicketsSubscription;
